import { useEffect } from 'react';

import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { useProcessTradeDate } from '@/hooks/trades';
import { canPerform } from '@/features/trades/hooks';
import { formatDate, todayUtcKey } from '@/utils/format';
import { isTradeDateInFuture } from '@/utils/trades/trade-date';
import { TRADE_STATUS_LABELS, type PositionDetail } from '@/types/trades.types';

interface ProcessTradeDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
  /** The `useProcessTradeDate` mutation handle, composed by useTradeActions. */
  processTradeDate: ReturnType<typeof useProcessTradeDate>;
  /** Run after a successful open, before close — the page passes `handleRefresh`
   *  so detail / activity / accruals refetch (parity with the other modals). */
  onSuccess?: () => void;
}

/**
 * LR-544 "Open now" dialog for the pending trade-detail action bar. Manually
 * fires the Trade Date action (mirrors what the hourly cron does) so the
 * operator doesn't have to wait for the next run. Built on the shared
 * `ConfirmDialog` + an LR-478 before→after status preview so the outcome is
 * self-explanatory before commit:
 *  - T+0 (open settlement date already reached) → Active
 *  - otherwise                                  → Pending Settlement (BO hold)
 *
 * A BO failure is non-fatal on the backend (the position stays pending); its text
 * is surfaced inline via the ConfirmDialog `error` slot — 400 when BO refused the
 * hold, 502 when BO gave no usable answer (LR-581), 409 when the posting guard is
 * holding this trade and 503 when the guard store is unreachable (LR-582; in both
 * of those nothing was sent to BO). All four arrive as `{detail}` and render
 * through `getErrorMessage`, which is status-agnostic — no branching needed here.
 * Force-open is out of scope: for a future trade date the button is already
 * disabled upstream (TradeActionButtons), and this dialog re-explains + blocks
 * Confirm as a belt.
 */
export function ProcessTradeDateModal({
  isOpen,
  onClose,
  trade,
  processTradeDate,
  onSuccess,
}: ProcessTradeDateModalProps) {
  const { mutate, isPending, error, reset } = processTradeDate;

  // Reset on (re)open so a prior error never bleeds into a fresh attempt.
  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  // UTC "today" to mirror the backend's future-TD / T+0 decision (server uses UTC).
  const today = todayUtcKey();
  const isFutureTradeDate = isTradeDateInFuture(trade.trade_date);
  // T+0 shortcut: open settlement date (settlement_date) already reached.
  const isTPlusZero = !!trade.settlement_date && trade.settlement_date <= today;
  const afterStatus = isTPlusZero ? 'active' : 'pending_open_settlement';

  const handleConfirm = () => {
    // Submit-path guard (ACTN-02): re-check validity + the future-TD gate
    // against the freshly-loaded trade so a stale/keyboard/race submit no-ops.
    if (!canPerform(trade, 'process_trade_date') || isFutureTradeDate) return;
    mutate(
      { id: trade.id },
      { onSuccess: () => { onSuccess?.(); onClose(); } },
    );
  };

  return (
    <ConfirmDialog
      show={isOpen}
      onClose={onClose}
      title="Open now"
      confirmLabel="Open now"
      isLoading={isPending}
      onConfirm={handleConfirm}
      confirmDisabled={isFutureTradeDate}
      error={error}
      errorFallback="Could not open the trade. Please try again."
      // LR-544 r2 (kvi P2): the verbose BO response has to stay visible past
      // getErrorMessage's 500-char cap (acceptance 3).
      showResponseDetail
    >
      <p className="text-sm text-gray-500">
        Fire the Trade Date action for position <strong>{trade.id}</strong> ({trade.symbol_id})
        now, instead of waiting for the hourly job. This places the BackOffice
        hold{isTPlusZero ? ' and settles the position immediately (T+0)' : ''}.
      </p>

      {isFutureTradeDate ? (
        <div
          className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          aria-live="polite"
        >
          Trade date is {formatDate(trade.trade_date)} — the position opens
          automatically on Trade Date. Open&nbsp;now is only available once the
          Trade Date has arrived.
        </div>
      ) : (
        /* LR-478 S3: before→after preview so the outcome is clear before commit. */
        <p id="open-now-preview" className="mt-4 text-[11px] text-gray-600" aria-live="polite">
          Status:{' '}
          <span className="text-gray-500">{TRADE_STATUS_LABELS.pending}</span>
          {' → '}
          <span className="font-semibold text-gray-900">{TRADE_STATUS_LABELS[afterStatus]}</span>
        </p>
      )}
    </ConfirmDialog>
  );
}
